import { freshSave, type SaveData } from "./save";
import { DISCOVERIES } from "./guide/content";
import { freshGuide, veteranGuide } from "./guide/engine";
import { canPlaceDecorAt, extendStreamsAcrossExpansion, MAX_PLOT_CELLS, WATER_DIV } from "./garden";
import { decorDef, hostSlots, type DecorId, type DecorPlacement } from "./decor";
import type { TerrainType } from "./terrain";
import { harvestAchievementId } from "./achievementFamilies";
import { flower, baseGrowSeconds, type FlowerId } from "./flowers";
import { LEGEND_THRESHOLD } from "./legendPlants";
import { ACHIEVEMENTS } from "./achievements";
import { xpForLevel, bugXpForLevel } from "./level";
import { ROBOT_UNLOCK_LEVEL, ROBOT_SHIFT_MS } from "./robot";
import { activeSubscription } from "./subscription";
import { startingPlot, type Plot } from "../render/plot";
import { gameNow } from "./gameClock";
import { DAY_MS } from "./weather";
import { showcaseSave } from "./showcase";

/**
 * SCREENSHOT MODE — a DEV-ONLY bootstrap for the Playwright screenshot harness (scripts/shot.mjs), so an
 * automated headless render can force a specific scene + a deterministic, populated save. This lets us
 * capture reproducible PNGs of the game for visual iteration.
 *
 * Activated ONLY when BOTH: `import.meta.env.DEV` (Vite dev server, never a production build) AND a `?shot=`
 * query param is present. It is completely inert in the shipped game — no player can trigger it.
 *
 *   /play?shot=greenhouse     → force the greenhouse scene, populated preset save
 *   /play?shot=garden         → force the garden scene, populated preset save
 *   /play?shot=greenhouse&raw → use the real (empty) save instead of the preset
 */

function params(): URLSearchParams | null {
  if (!import.meta.env?.DEV) return null;   // `?.`: under node (the unit tests) there is no Vite env
  try {
    const p = new URLSearchParams(window.location.search);
    return p.has("shot") ? p : null;
  } catch { return null; }
}

/** The scene to force for a screenshot, or null when not in shot mode. */
export function shotScene(): "garden" | "greenhouse" | "powerplant" | null {
  const p = params();
  const s = p?.get("shot");
  return s === "garden" || s === "greenhouse" || s === "powerplant" ? s : null;
}

/** True if the harness wants the game bypassed straight into a deterministic populated save (default),
 *  vs the real/empty save (`&raw`). Null when not in shot mode. */
export function shotActive(): boolean { return params() != null; }

/** An initial camera x-offset (world px) for the shot, so we can frame a specific part of the scene (e.g.
 *  `&cam=-200` to pan left to the greenhouse's computer terminal). 0 when unset / not in shot mode. */
export function shotCamX(): number {
  const v = params()?.get("cam");
  const n = v != null ? Number(v) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** `&res=<n>` → render at this resolution instead of the game's 1.5× cap (sharedApp.ts), so a marketing
 *  shot can be drawn natively at a high pixel density from a small window (scripts/showcase.mjs). Clamped to
 *  1..4. Null when unset — the cap stands. Dev-only, like the rest of shot mode. */
export function shotResolution(): number | null {
  const n = Number(params()?.get("res"));
  return Number.isFinite(n) && n > 0 ? Math.max(1, Math.min(4, n)) : null;
}

/** `&open=compost` → open the composter's modal on load, for a screenshot of it. Dev-only. */
export function shotOpenCompost(): boolean {
  return params()?.get("open") === "compost";
}

/** `&fish=fight` → immediately cast + hook a fish so the in-world fishing FIGHT HUD renders for a shot.
 *  `&fish=cast` → just cast (line follows cursor, waiting). null otherwise. Dev-only. */
export function shotFish(): "fight" | "cast" | null {
  const v = params()?.get("fish");
  return v === "fight" || v === "cast" ? v : null;
}

/** `&sprinklers[=<count>]` → place that many sprinkler pairs (spread across racks/tiers, default 6) and
 *  force every one of them SPRAYING, bypassing the automation rule and the grid. The spray is the most
 *  expensive per-frame thing the rack layer draws, and the default preset places none and runs none — so
 *  without this the perf probe reports a greenhouse with zero sprinkler cost and a spray optimisation
 *  cannot be measured at all. Returns 0 when unset. Dev-only, like the rest of shot mode. */
export function shotSprinklers(): number {
  const p = params();
  if (!p || !p.has("sprinklers")) return 0;
  const n = Number(p.get("sprinklers"));
  return Number.isFinite(n) && n > 0 ? Math.min(60, Math.floor(n)) : 6;
}

/** The shelf keys `&sprinklers` places — filled tier-by-tier across the racks the shot asks for, so a
 *  count of 6 lands on 2 racks × 3 tiers rather than piling onto one rack. */
export function shotSprinklerKeys(): string[] {
  const want = shotSprinklers();
  if (want === 0) return [];
  const p = params()!;
  const racks = (() => { const n = Number(p.get("racks")); return Number.isFinite(n) && n >= 1 ? n : 2; })();
  const keys: string[] = [];
  for (let t = 0; t < 3 && keys.length < want; t++) {
    for (let r = 0; r < racks && keys.length < want; r++) keys.push(`${r}-${t}`);
  }
  return keys;
}

/** `&weather=<type>` → force a weather type for the shot (e.g. heavyRain) so rain/splashes render
 *  deterministically. Returns the raw string; the caller runs it through coerceWeatherType, so an old URL
 *  naming a removed type (`bright`) forces `clear` rather than being dropped. */
export function shotWeather(): string | null {
  return params()?.get("weather") ?? null;
}

/** `&time=<0..1>` (or the keywords `day`=0.3 / `night`=0.8 / `dusk`=0.56 / `dawn`=0.06) → freeze the
 *  day/night phase for the shot, so time-sensitive plants (e.g. the Nightglow Mushroom) render at a
 *  deterministic time of day. Returns null when unset. */
export function shotTimeOverride(): number | null {
  const v = params()?.get("time");
  if (!v) return null;
  const key: Record<string, number> = { dawn: 0.06, day: 0.3, dusk: 0.56, night: 0.8 };
  if (v in key) return key[v]!;
  const n = Number(v);
  return Number.isFinite(n) ? ((n % 1) + 1) % 1 : null;
}

/** `?shot=garden&butterflytest` → seed a PINE tree + a daisy placed BEHIND-and-beside it, so App can rest a
 *  butterfly on that daisy and we can verify the butterfly is occluded by the tall tree (the reported bug).
 *  Dev-only. The daisy/pine positions here are mirrored by App's seeded butterfly snapshot. */
export function shotButterflyTest(): boolean {
  return params()?.has("butterflytest") ?? false;
}
/** `?shot=greenhouse&breakbake=<flowerId>` → that species' plant bake THROWS (dev only), so the smoke test can
 *  prove a broken renderer degrades to one console error and a missing plant instead of a frozen scene
 *  loader or a stopped ticker. */
export function shotBreakBake(): string | null {
  return params()?.get("breakbake") ?? null;
}
/** `?shot=garden&placing=<flowerId>` → start in garden place mode with that species' placement ghost showing
 *  under the cursor, so the ghost's per-frame cost can be probed headlessly. Dev-only. */
export function shotPlacing(): string | null {
  return params()?.get("placing") ?? null;
}
/** `?shot=garden&placingdecor=<decorId>` → the same for the DECOR placement ghost (and its blocked-footprint
 *  red, when the cursor is parked over something). Dev-only. */
export function shotPlacingDecor(): string | null {
  return params()?.get("placingdecor") ?? null;
}
/** `&bath` sub-flag of butterflytest → the occluder is a BIRD BATH (tall decor) instead of a pine, to verify
 *  tall-decor occlusion of a resting butterfly. */
export function shotBathTest(): boolean {
  const p = params();
  return !!p && p.has("butterflytest") && p.has("bath");
}

/** `?shot=garden&pickup` → make the floating bottle + note spawn almost immediately (instead of every few
 *  minutes), so a screenshot/verify can catch them on screen. Dev-only. */
export function shotPickup(): boolean {
  return params()?.has("pickup") ?? false;
}

/** `?shot=garden&bugtest` → seed a fan of MOVING insects at fixed headings + full speed so a screenshot shows
 *  the iso squish (screen-vertical flatten) + motion squash-&-stretch at every travel direction. Dev-only. */
export function shotBugTest(): boolean {
  return params()?.has("bugtest") ?? false;
}

/** `?shot=garden&stream&shadowtest` → seed a fixed row of FISH SHADOWS at known spots across both water
 *  types (pond bulb + stream channel) at a spread of sizes and headings, instead of waiting on the random
 *  spawn cadence. The shadows are the thing a player hunts for, so their legibility has to be checkable in
 *  a shot under every weather/time; the real sim spawns them minutes apart, which a screenshot can't catch.
 *  Dev-only. */
export function shotShadowTest(): boolean {
  return params()?.has("shadowtest") ?? false;
}

/** `?shot=garden&mirror` → render the App as a SPECTATE MIRROR (readOnly) + expose window.__applyMirror so a
 *  test can apply a canned UI-mirror snapshot and verify modals/ghosts render exactly. Dev-only. */
export function shotMirror(): boolean {
  return params()?.has("mirror") ?? false;
}

/** `?shot=garden&odds=insect` (or `=fish`) → auto-open that Odds modal on load so a screenshot can capture it.
 *  Dev-only; returns "insect" | "fish" | null. */
export function shotOddsModal(): "insect" | "fish" | null {
  const v = params()?.get("odds");
  return v === "insect" || v === "fish" ? v : null;
}

/** `?shot=greenhouse&pole` → auto-open the POWER POLE modal on load so a screenshot can capture the itemised
 *  bill. Dev-only. */
export function shotPoleModal(): boolean {
  return params()?.has("pole") ?? false;
}

/** Achievement ids worth at least `target` achievement points, taken in list order. Used by `?ptplayer` to put the
 *  shot/smoke player at a chosen point score without naming ids that a retune would invalidate. */
function pointSeedIds(target: number): string[] {
  const ids: string[] = [];
  let sum = 0;
  for (const a of ACHIEVEMENTS) {
    if (sum >= target) break;
    if (a.enabled === false) continue;
    ids.push(a.id);
    sum += Math.max(1, Math.min(10, Math.round(a.points ?? 1)));
  }
  return ids;
}

/** `?shot=garden&pttrack` → auto-open the PLAYER-FACING point reward track on load, for a screenshot and for
 *  the smoke test's claim flow. Distinct from `ptrack`, which opens the DEV EDITOR for the same data. */
export function shotPointTrackPlayer(): boolean {
  return params()?.has("pttrack") ?? false;
}

/** `?shot=garden&talents[=<level>]` → auto-open the BUG CATCHING TALENTS modal on load, with the save seeded
 *  to that Naturalist level (default 32) and a spread of picks already taken, so a screenshot shows all three
 *  card states at once: chosen, takeable, and locked. Dev-only. */
export function shotTalentsModal(): boolean {
  return params()?.has("talents") ?? false;
}
/** `&talentscroll=<px>` → scroll the talent tree down that far, so the deep (epic / legendary) rungs and the
 *  locked tiers can be screenshotted; they sit below the fold otherwise. 0 when unset. Dev-only. */
export function shotTalentScroll(): number {
  const n = Number(params()?.get("talentscroll"));
  return Number.isFinite(n) ? n : 0;
}

/** `?shot=garden&tutorial` → the populated shot save with the guide's First Harvest NOT yet done, so its cards and
 *  coachmarks render over a shot. Without it a shot save is a veteran (no tutorial). Dev-only. */
export function shotTutorial(): boolean {
  return params()?.has("tutorial") ?? false;
}

/** Any of the reward-track fixtures (`ptplayer`, `pttake`, `pttrack`, `ptrack`) → the REWARD TRACK is switched
 *  ON for this dev session even while it is off for players (sim/pointTrack `rewardTrackEnabled`), so the
 *  smoke test keeps proving the dormant feature — claims, reloads, the subscriber hold — still works. */
export function shotRewardTrack(): boolean {
  const p = params();
  return !!p && ["ptplayer", "pttake", "pttrack", "ptrack"].some((k) => p.has(k));
}

/** `?shot=garden&ptrack` → auto-open the POINT REWARD TRACK editor on load so a screenshot can capture the
 *  ladder + its warnings. Dev-only. */
export function shotPointTrackModal(): boolean {
  return params()?.has("ptrack") ?? false;
}

/** `&quiet` → suppress EVERY automatic celebration (achievement unlocks AND level-ups) for the whole
 *  session. The populated preset trips achievements whose payouts carry xp, so without this a default shot
 *  lands under "ACHIEVEMENT UNLOCKED" and then "Fishing Level 3!". scripts/shot.mjs passes it by default.
 *  An explicit `&celebrate=` still raises its one modal. Dev-only. */
export function shotQuiet(): boolean {
  return params()?.has("quiet") ?? false;
}

/** `&celebrate=<spec>` → on mount, raise a celebration pop-up so the modal can be screenshot. Specs:
 *  `garden:<lvl>` / `fishing:<lvl>` (a level-up modal for that track+level) or `ach:<id>` (an achievement
 *  modal). Returns the parsed spec or null. Dev-only (params() already gates on import.meta.env.DEV). */
export function shotCelebrate(): { kind: "level"; track: "gardening" | "fishing"; level: number } | { kind: "ach"; id: string } | null {
  const v = params()?.get("celebrate");
  if (!v) return null;
  const [a, b] = v.split(":");
  if (a === "garden" || a === "gardening") return { kind: "level", track: "gardening", level: Math.max(1, Number(b) || 2) };
  if (a === "fishing" || a === "fish") return { kind: "level", track: "fishing", level: Math.max(1, Number(b) || 2) };
  if (a === "ach" && b) return { kind: "ach", id: b };
  return null;
}

/** The `?shot=garden&fencetest` layout — see the preset in shotSave. */
const FENCETEST_KEYS: string[] = [
  // a 5-segment run along one boundary (the repeat test)…
  "-2,-2,v", "-1,-2,v", "0,-2,v", "1,-2,v", "2,-2,v",
  // …turning a CORNER at post (3,-2) and running back toward the viewer (4 segments)…
  "3,-2,h", "3,-1,h", "3,0,h", "3,1,h",
  // …closing a second CORNER at post (3,2) and running back the other way,
  "2,2,v", "1,2,v", "0,2,v",
  // plus a TEE: a spur leaving the long top run at post (0,-2), where three rails meet one post.
  "0,-2,h", "0,-1,h",
  // …and a detached L in the OTHER diagonal orientation, meeting at post (-2,1) with arms E+S.
  // The three corners above are all W+S/W+N — the orientations whose shared post is the NEAR end
  // of an incident segment — and an E+S corner is the one the old ownership rule dropped
  // entirely: both of its halves sort in front of the post and both were disqualified at 2 arms,
  // so nobody drew it. Without this pair in the preset the fence's own corner test missed it.
  "-2,1,v", "-2,1,h",
];

/**
 * A deterministic, POPULATED save for screenshots so scenes aren't empty: a few grown plants in the
 * greenhouse pots + some placed plants/decor in the garden, tutorial marked done (so no overlay covers the
 * shot), plenty of coins. `plantedAt` is set well in the past so plants render fully grown. Returns null
 * when not in shot mode (or `&raw` requested) so the normal save loads.
 */
export function shotSave(): SaveData | null {
  const p = params();
  if (!p || p.has("raw")) return null;
  // A fixed reference time so growth is deterministic (avoid Date.now so shots are identical run-to-run).
  const longAgo = 1_600_000_000_000;   // ~2020, far enough back that everything reads as mature
  const base = freshSave();
  const save = {
    // a synthetic preset is not a returning player: the one-time reward catch-up (App) must not raise its card
    // over the scene being shot or tested. The `&raw` path loads a REAL save, where the flag is the save's own.
    rewardCatchup: 1,
    ...base,
    coins: 99999,
    // Gardening L8 (smoke relies on it: shelf #3 just open, compost/sprinklers/lights still locked). Seeded from the
    // curve, not as a literal, so a re-curve cannot silently move the preset to another level (1,200 was L8 on curve 1).
    xp: xpForLevel(8),
    // No guide over the shot — no chapter, and every Discovery tip already seen (one fires at load and would sit over
    // the shot) — unless `&tutorial` asks for First Harvest to run over the populated save.
    guide: shotTutorial() ? freshGuide() : { ...veteranGuide(), seen: DISCOVERIES.map((d) => d.id) },
    // `?shot=garden&ptplayer` → a player MID-TRACK on the point reward track: enough unlocked achievements to be
    // past several milestones with none of them claimed, which is the state the track's window slides in and
    // the state its safety-net grid exists for. The ids are DERIVED from the live achievement list rather than
    // hard-coded, so this keeps seeding the right score after any retune; `pttake` additionally claims the
    // first milestone so a shot (or the smoke test) can see the claimed-vs-unclaimed pair side by side.
    ...(p.has("ptplayer")
      ? { unlockedAchievements: pointSeedIds(Number(p.get("ptplayer")) || 420),
          claimedMilestones: p.has("pttake") ? ["pt25"] : [] }
      : {}),
    // `?shot=…&subscribed` → the save starts with an ACTIVE monthly subscription (sim/subscription.ts), so a
    // shot or the smoke test can see the subscriber side of a gate without driving the dev menu. No period
    // end: "on until something turns it off", which is what `activeSubscription()` with no argument means.
    // The DEFAULT is unsubscribed, deliberately — a preset that quietly subscribes everyone would hide the
    // held-reward state, which is the half of this feature that can hurt a paying player if it regresses.
    ...(p.has("subscribed") ? { subscription: activeSubscription() } : {}),
    // `?shot=greenhouse&compost` → TWO brewed batches sitting in the compost bin, ready to load into a pot.
    // Reaching this state by hand means running the whole composter loop (fill the barrel, seal it, wait out
    // out the ~2-minute ferment, collect), which the smoke test cannot drive inside a step — so the bin is seeded
    // directly. The two batches are deliberately DIFFERENT (one quickBloom, one retain) so a test that moves
    // a batch between pots can tell which one arrived rather than just counting keys.
    ...(p.has("compost")
      ? { compostBin: [
            // amounts are whole percent (a batch's unit) — these once read 0.25 / 0.3, i.e. a quarter of 1%.
            { effects: [{ type: "quickBloom" as const, amount: 25, min: 10, max: 40 }] },
            { effects: [{ type: "retain" as const, amount: 30, min: 10, max: 50 }] },
          ] }
      : {}),
    // `?shot=greenhouse&compostready` → a Level-3 scrap composter mid-batch (seconds from done) with 30 scraps
    // stored and three finished composts on the shelf, plus two pots with compost in: every state the new
    // composter modal and the pot crumbs have, without a real few-minute wait.
    ...(p.has("compostready")
      ? { equipment: { compostMachine: 1 }, compostTier: 2,
          composter: {
            on: true, scraps: 30, made: 3, best: 4, sinceThree: 0, sinceFive: 0, lastAt: gameNow(),
            job: { tier: 2, seed: 77, progressMs: 170_000 },
            finished: [
              { id: "cr1", rank: 1, effects: [{ type: "quickBloom" as const, amount: 7, min: 5, max: 10 }] },
              { id: "cr3", rank: 3, effects: [{ type: "double" as const, amount: 12, min: 10, max: 14 }, { type: "retain" as const, amount: 28, min: 24, max: 35 }] },
              { id: "cr4", rank: 4, effects: [{ type: "value" as const, amount: 27, min: 23, max: 30 }, { type: "xp" as const, amount: 25, min: 23, max: 30 }] },
            ],
          },
          potCompost: {
            "0-0-2": { rank: 2, effects: [{ type: "pollinate" as const, amount: 11, min: 8, max: 13 }] },
            "0-1-3": { rank: 1, effects: [{ type: "quickBloom" as const, amount: 6, min: 5, max: 10 }] },
          } }
      : {}),
    // `?shot=greenhouse&legend=<species>` → that species is LEGEND-ranked (10,000 harvested), so every one
    // of it sparkles. For eyeballing the effect and for the perf probe's worst case — pair it with
    // `&species=…` repeated to fill the house with the same plant.
    ...(p.get("legend")
      ? { unlockedAchievements: [harvestAchievementId(p.get("legend")! as FlowerId, LEGEND_THRESHOLD)] }
      : {}),
    // `?shot=greenhouse&robot` → own the GARDEN HELPER with standing orders on the BOTTOM shelf, and empty
    // those pots so the planting orders are immediately actionable. The bottom row is chosen because the
    // shot presets fill the upper shelves, and a planting order on an occupied pot is legal but does
    // nothing — which is exactly the state that made the first hand-test look like a broken robot.
    ...(p.has("robot")
      ? {
          robot: true,
          // ON SHIFT (sim/robot.ts): it works only inside one, so without this the preset's robot sits on its
          // dock. `&robotshift=<seconds>` starts it with only that much of the shift LEFT — the lever for
          // watching it stop at the bell with the tab open.
          // `&robotshift=off` leaves it never switched on (start 0) — the shift modal's OFF state, lever showing.
          ...(() => {
            if (p.get("robotshift") === "off") return { robotShiftReq: 0, robotShiftStart: 0 };
            const left = Number(p.get("robotshift"));
            const start = gameNow() - (p.has("robotshift") && Number.isFinite(left) ? ROBOT_SHIFT_MS - left * 1000 : 0);
            return { robotShiftReq: start, robotShiftStart: start };
          })(),
          // SEEDS TO SOW WITH. Without these the planting orders are legal but dormant — the robot holds
          // off until seeds exist, which is the designed behaviour and exactly what the first version of
          // this preset accidentally tested.
          seeds: { rose: 12, tulip: 12 },
          robotOrders: {
            // 0-2-2 and 0-2-3 are EMPTY in the default preset, so the planting orders are actionable at
            // once; 0-2-0 is planted long ago (hence ripe), so the harvest order fires immediately too.
            "0-2-2": { plant: "rose", water: true },
            "0-2-3": { plant: "tulip" },
            "0-2-0": { harvest: true },
          },
        }
      : {}),
    // `?shot=greenhouse&robotbuy` → a player AT ROBOT_UNLOCK_LEVEL who does NOT own the Garden Helper yet:
    // the exact state the helper card on the terminal's Gear tab exists for, and the state the unlock
    // announcement fires in. `robotAnnounced` is left FALSE on purpose so the HUD callout is up too — the
    // discovery path and the purchase path are the same test. The preset's 99,999 coins are well over
    // ROBOT_PRICE, so the buy key is live rather than greyed out. `&robotbuy=<level>` seeds a different
    // gardening level instead — `robotbuy=27`, one under the gate, is the save that must NOT be able to buy.
    ...(p.has("robotbuy")
      ? { xp: xpForLevel(Number(p.get("robotbuy")) || ROBOT_UNLOCK_LEVEL), robot: false, robotAnnounced: false }
      : {}),
    // `?shot=greenhouse&potskin=<id>` → give a spread of pots that skin (skins are per-pot now) so the swap
    // can be verified in a shot. Also marks every pot-skin achievement unlocked so all skins read as owned.
    ...(p.get("potskin")
      ? { potSkins: (() => {
            const id = p.get("potskin")!, m: Record<string, string> = {};
            // first two racks × 3 tiers × 4 pots
            for (let r = 0; r < 2; r++) for (let t = 0; t < 3; t++) for (let c = 0; c < 4; c++) m[`${r}-${t}-${c}`] = id;
            return m;
          })(),
          unlockedAchievements: ["first_harvest", "green_thumb", "master_gardener", "botanist", "angler"],
          harvestCount: 200 }
      : {}),
    // `?shot=greenhouse&houseskin=<id>` → force the cosmetic SHELL skin (sim/houseSkins.ts), so each
    // building material can be screenshotted on the same backdrop at the same time of day. Pair it with
    // `&time=night` / `&time=day` to check a skin under the interior light pass. Unknown ids fall back to
    // the default at draw time, so a typo is a safe no-op rather than a crash. (Dev only.)
    // Every achievement pre-marked so NONE pop: the preset save trips a chain of them and each lands
    // centre-screen over the building we came to shoot (same reason `&talents` does it).
    ...(p.get("houseskin")
      ? { selectedBuildingSkin: p.get("houseskin")!, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=greenhouse&floorskin=<id>` → same, for the FLOOR skin registry. Achievements pre-marked for
    // the same reason `houseskin` does it: the preset save trips a chain of them and the unlock dialog
    // lands dead centre — over the floor we came to shoot.
    ...(p.get("floorskin")
      ? { selectedFloorSkin: p.get("floorskin")!, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=greenhouse&shelfskin=<id>` → same, for the RACK skin registry (sim/shelfSkins.ts).
    ...(p.get("shelfskin")
      ? { selectedShelfSkin: p.get("shelfskin")!, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=greenhouse&lights` → fit a grow light to every shelf tier of the first two racks, so the
    // interior LIGHT PASS (lightGlowG, which adds over the glazing) can be checked against a shell skin.
    // A skin that reads at noon can still blow out under a grow light, and this is how that is caught.
    ...(p.has("lights")
      ? { growLights: (() => { const k: string[] = []; for (let r = 0; r < 2; r++) for (let t = 0; t < 3; t++) k.push(`${r}-${t}`); return k; })(),
          gridPower: true }
      : {}),
    // `?shot=greenhouse&stale=<hours>` → a save whose POWER METER was last billed <hours> ago (default 2),
    // with grid power ON and the lights fitted. This is the AWAY-TIME fixture: the game has no other way to
    // produce a save with a gap in it, because the harness can only ever load a save that was just written.
    // The first billing tick after load must charge that whole gap (see the catch-up in App.tsx); it used to
    // charge ~1 s because the accrual ref was seeded from gameNow() instead of from the save.
    ...(p.has("stale")
      ? { lastPowerAccrualMs: gameNow() - Math.max(0, Number(p.get("stale")) || 2) * 3_600_000,
          growLights: (() => { const k: string[] = []; for (let r = 0; r < 2; r++) for (let t = 0; t < 3; t++) k.push(`${r}-${t}`); return k; })(),
          lightManual: (() => { const m: Record<string, boolean> = {}; for (let r = 0; r < 2; r++) for (let t = 0; t < 3; t++) m[`${r}-${t}`] = true; return m; })(),
          gridPower: true }
      : {}),
    // `?shot=greenhouse&systems` → a SPARSE, deliberately gappy spread of automated hardware, for the
    // Systems tab's type → rack → shelf tree. Lights sit on racks 1 and 3 only, and only on some of their
    // tiers; sprinklers sit on one rack alone. The gaps are the point: rack 2 owns no light and the tree
    // must not draw a branch for it, and a rack that owns one light must show one row and not three.
    // (`&lights` is the opposite fixture — every tier of the first two racks, for the light PASS.)
    ...(p.has("systems")
      ? { growLights: ["0-0", "0-2", "2-1"], sprinklers: ["1-0", "1-1"], gridPower: true }
      : {}),
    // `?shot=garden&talents[=<level>]` → a MID-TREE Naturalist: enough level for six tiers, four of them
    // already picked. That is the state the talent screen has to read well in — every card state on screen
    // together — and the empty preset save shows none of them.
    ...(p.has("talents")
      ? { bugXp: bugXpForLevel(Math.max(5, Math.min(50, Number(p.get("talents")) || 32))),
          bugTalents: { 0: "wideNet", 1: "pounce", 2: "restWatch", 4: "piercer2" },
          // Every achievement pre-marked so NONE pop. The preset save trips a chain of them longer than the
          // harness's six close-button clicks, and each lands centre-screen over the modal we came to shoot.
          unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=greenhouse&sprinklers[=<n>]` → n sprinkler pairs placed and (via shotSprinklers in the
    // greenhouse hook) all forced to spray. The perf probe's worst case for the droplet system.
    // Every achievement pre-marked so NONE pop: the preset trips a chain of them and each lands centre-
    // screen behind a blur, over the spray we came to look at (same reason `&talents` does it).
    ...(shotSprinklers() > 0
      ? { sprinklers: shotSprinklerKeys(), gridPower: true, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    rackCount: (() => { const n = Number(p.get("racks")); return Number.isFinite(n) && n >= 1 ? n : 2; })(),
    // `?shot=greenhouse&grid` → grid power ON + the composter, to verify the power pole (wired to the
    // greenhouse) and its grid-on "live" state. (Dev only.)
    ...(p.has("grid")
      ? { equipment: { compostMachine: 1 }, gridPower: true }
      : {}),
    // `?shot=greenhouse&compost=<0..4>` → own the composter at that TIER, so each rung of the upgrade
    // ladder can be screenshotted in its bay under the terminal. (Dev only.)
    ...(p.has("compost")
      ? { equipment: { compostMachine: 1 }, compostTier: Math.max(0, Math.min(4, Number(p.get("compost")) || 0)) }
      : {}),
    // a few caught fish so the Inventory's Fish section isn't empty in a shot. (Dev-only.)
    fishCaught: { minnow: 3, bass: 1, koi: 2, trout: 1, salmon: 1, sunfish: 2, bluegill: 1, crappie: 1, goldenTench: 1, carp: 1 },
    // harvested cut-flower stock + collected insects so the sell hub's list isn't empty in a shot. (Dev.)
    stock: { daisy: 8, tulip: 5, sunflower: 3, lavender: 4, rose: 2 },
    butterfliesCaught: { monarch: 3, swallowtail: 2, ladybug: 5, dragonfly: 1, firefly: 4 },
    // Clue reveals, deliberately mixing BOTH formats so a guide shot shows every state at once:
    //   • a BARE id ("bass"/"monarch") — the pre-per-condition save shape, which coerceReveals grandfathers
    //     to "all conditions legible" (see sim/clueReveal.ts). Keeps the migration path on screen in a shot.
    //   • per-condition entries — a PARTIALLY read species, the new normal: some bullets legible, the rest
    //     still smudged.
    revealedFish: ["bass", "pike:habitat", "pike:needsLure"],
    revealedBugs: ["monarch", "commonBlue:day"],
    // GREENHOUSE: fill the front rack's pots with a couple of species (fully grown). `&premium=<id>` (or the
    // `&vo` alias for voidOrchid) swaps in that premium plant (dev preview in the real greenhouse).
    plantings: (p.get("premium") || (p.has("vo") ? "voidOrchid" : "")) ? (() => {
      const sp = p.get("premium") || "voidOrchid";
      return {
        "0-0-0": { species: sp, plantedAt: longAgo },
        "0-1-0": { species: sp, plantedAt: longAgo },
        "0-1-1": { species: sp, plantedAt: longAgo },
      };
    })() : p.get("species") ? (() => {
      // dev: `&species=a,b,c,…` fills pots (rack 0, then rack 1) with the listed species in order, fully
      // grown — to eyeball many plants at once (e.g. the game-wide stroke sweep).
      const list = p.get("species")!.split(",").map((s) => s.trim()).filter(Boolean);
      const out: Record<string, { species: string; plantedAt: number }> = {};
      let i = 0;
      for (let r = 0; r < 2 && i < list.length; r++)
        for (let t = 0; t < 3 && i < list.length; t++)
          for (let c = 0; c < 4 && i < list.length; c++, i++)
            out[`${r}-${t}-${c}`] = { species: list[i]!, plantedAt: longAgo };
      return out;
    })() : p.has("strokemix") ? {
      // dev: daisy / marigold / rosemary side-by-side to eyeball the game-wide STROKE trial.
      "0-0-0": { species: "daisy", plantedAt: longAgo },
      "0-0-1": { species: "marigold", plantedAt: longAgo },
      "0-0-2": { species: "rosemary", plantedAt: longAgo },
      "0-1-0": { species: "daisy", plantedAt: longAgo },
      "0-1-1": { species: "marigold", plantedAt: longAgo },
      "0-1-2": { species: "rosemary", plantedAt: longAgo },
    } : {
      "0-0-0": { species: "daisy", plantedAt: longAgo },
      "0-0-1": { species: "daisy", plantedAt: longAgo },
      "0-1-0": { species: "tulip", plantedAt: longAgo },
      "0-1-1": { species: "tulip", plantedAt: longAgo },
      "0-1-2": { species: "tulip", plantedAt: longAgo },
      "0-2-0": { species: "tulip", plantedAt: longAgo },
      "0-2-1": { species: "tulip", plantedAt: longAgo },
    },
    // `?shot=greenhouse&thirsty&racks=<n>` → EVERY pot of every rack planted and its soil dry for two game
    // days, so every plant is THIRSTY (sim/moisture isThirsty) and pulses. The perf probe's worst case for
    // the thirst tint (racks=5 → 60 pots); the default preset has no dry pot at all. (Dev only.)
    ...(p.has("thirsty") ? (() => {
      const racks = (() => { const n = Number(p.get("racks")); return Number.isFinite(n) && n >= 1 ? n : 2; })();
      const dryAt = gameNow() - 2 * DAY_MS;
      const species = ["daisy", "tulip", "marigold", "rosemary"];
      const plantings: Record<string, { species: string; plantedAt: number }> = {};
      const potMoisture: Record<string, { level: number; at: number; drySince: number }> = {};
      let i = 0;
      for (let r = 0; r < racks; r++)
        for (let t = 0; t < 3; t++)
          for (let c = 0; c < 4; c++, i++) {
            const key = `${r}-${t}-${c}`;
            plantings[key] = { species: species[i % species.length]!, plantedAt: longAgo };
            potMoisture[key] = { level: 0, at: dryAt, drySince: dryAt };
          }
      return { plantings, potMoisture, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) };
    })() : {}),
    // `?shot=greenhouse&away=<hours>` → a save written <hours> ago (default 2): `savedAt`, the grid meter and
    // three tulips' banked growth all stamped then, grid on with the lights drawing. The AWAY-CAP fixture —
    // the first tick must credit min(<hours>, 4) of absence, in slices. Pot 0-0-0 is DRY throughout (grows
    // at exactly 0.6×, so its gain measures credited time), 0-0-1 starts WET and dries 30 min in (an in-game
    // day is 40 min), and 0-0-2 is ten minutes from ready so the away card has a plant to report.
    ...(p.has("away") ? (() => {
      const t0 = gameNow() - Math.max(0, Number(p.get("away")) || 2) * 3_600_000;
      const shelves: string[] = []; for (let r = 0; r < 2; r++) for (let t = 0; t < 3; t++) shelves.push(`${r}-${t}`);
      const nearlyReady = baseGrowSeconds(flower("tulip")) - 600;
      return {
        savedAt: t0, lastPowerAccrualMs: t0, gridPower: true, growLights: shelves,
        lightManual: Object.fromEntries(shelves.map((k) => [k, true])),
        plantings: {
          "0-0-0": { species: "tulip", plantedAt: t0, grown: 0, lastAt: t0 },
          "0-0-1": { species: "tulip", plantedAt: t0, grown: 0, lastAt: t0 },
          "0-0-2": { species: "tulip", plantedAt: t0, grown: nearlyReady, lastAt: t0 },
        },
        potMoisture: { "0-0-0": { level: 0, at: t0 }, "0-0-1": { level: 1, at: t0 }, "0-0-2": { level: 1, at: t0 } },
        unlockedAchievements: ACHIEVEMENTS.map((a) => a.id),
      };
    })() : {}),
    // `?shot=greenhouse&seedbag` → a bag of several seed kinds, for the Plant tool's seed tray. (Dev only.)
    ...(p.has("seedbag") ? { seeds: { daisy: 6, tulip: 4, rose: 3, lavender: 2, sunflower: 5 } } : {}),
    // `?shot=greenhouse&favored` → the FAVOURED-LIGHT fixture: rack 1 GROWING (not grown) daisies, tulips and
    // roses under top-tier lights in their favoured colours (white / magenta / red), so at night every pot
    // sends micro-XP orbs. Rack 2 shows the three switch states — on, switched OFF, and run by automation
    // (a night rule) — over mismatched plants. Pair with `&time=night`. (Dev only.)
    ...(p.has("favored") ? (() => {
      const now = gameNow();
      const shelves = ["0-0", "0-1", "0-2", "1-0", "1-1", "1-2"];
      const plantings: Record<string, { species: string; plantedAt: number; grown: number; lastAt: number }> = {};
      const rows: Record<string, string> = { "0-0": "daisy", "0-1": "tulip", "0-2": "rose", "1-0": "rose", "1-1": "daisy", "1-2": "tulip" };
      for (const sh of shelves) for (let c = 0; c < 4; c++) plantings[`${sh}-${c}`] = { species: rows[sh]!, plantedAt: now, grown: c * 60, lastAt: now };
      return {
        gridPower: true, growLights: shelves, sprinklers: ["0-0", "0-2", "1-1"],
        growLightColors: { "0-0": "white", "0-1": "magenta", "0-2": "red", "1-0": "white", "1-1": "blue", "1-2": "red" },
        growLightTiers: Object.fromEntries(shelves.map((k) => [k, 4])),
        lightManual: { "0-0": true, "0-1": true, "0-2": true, "1-0": true, "1-1": false },
        lightRules: { "1-2": { combiner: "and", conditions: [{ field: "timeOfDay", op: "eq", value: "night" }] } },
        plantings, unlockedAchievements: ACHIEVEMENTS.map((a) => a.id),
      };
    })() : {}),
    // GARDEN: a little decor so the garden shot isn't bare. (Unknown ids are dropped gracefully by coerce.)
    // A few EXTRA owned (beyond what's placed below) so the Inventory's decor items aren't all used up.
    // `oakTree: 1` backs the starter oak placed below, exactly as freshSave does: this whole object REPLACES
    // the fresh save's `decorOwned`, so leaving the oak out here re-creates the unbacked-placement bug (bag
    // reads `owned - placed` = 0 for a bought oak) in every preset the smoke test and the perf probes run on.
    decorOwned: { rock: 3, pinwheel: 2, gardenBench: 1, lantern: 2, birdBath: 1, oakTree: 1 },
    // Routed through the REAL placement check (legalDecor) against the fresh save's plot and starting pond:
    // the list used to be written in by hand and put the bench's legs AND the rock in the pond, a layout no
    // player could make. The pond is the ellipse in `startingWaterCells` (centre 1.6,1.1) — keep these west
    // and north of it.
    decor: legalDecor(base, [
      { id: "starter-oak", decor: "oakTree", gx: -1.8, gy: -1.8, seed: 7 },   // the starting oak (now decor)
      { id: "shot-rock", decor: "rock", gx: 0.6, gy: -1.4, seed: 3 },
      { id: "shot-pin", decor: "pinwheel", gx: -1.4, gy: 0.6, seed: 7 },
      { id: "shot-bench", decor: "gardenBench", gx: -2.2, gy: 2.2, seed: 1 },
    ]),
    // `?shot=garden&premium=<id>` (or `&vo` alias) → a couple of that premium plant placed in the garden.
    ...((p.get("premium") || (p.has("vo") ? "voidOrchid" : ""))
      ? (() => { const sp = p.get("premium") || "voidOrchid"; return { garden: [
          { id: "prem-1", species: sp, gx: -0.6, gy: -0.4 },
          { id: "prem-2", species: sp, gx: 0.8, gy: 0.6 },
        ] }; })()
      : {}),
    // `?shot=garden&garden=rosemary,asparagusFern` → those species placed in the garden, one per entry, in a
    // short diagonal so none overlap. For perf probes of placed garden plants (scripts/perfprobe.mjs EXTRA=).
    ...(p.get("garden")
      ? { garden: p.get("garden")!.split(",").map((s) => s.trim()).filter(Boolean).map((species, i) => (
          { id: `shot-plant-${i}`, species, gx: -0.8 + i * 0.6, gy: -0.6 + i * 0.4 })) }
      : {}),
    // `?shot=garden&decor=fountain,birdBath,…` → those decor items placed in a diagonal (REPLACES the default
    // decor set). For perf probes of decor bakes — animated items (fountain, wind chime) bake frame loops.
    ...(p.get("decor")
      ? { decor: p.get("decor")!.split(",").map((s) => s.trim()).filter(Boolean).map((decor, i) => (
          { id: `shot-decor-${i}`, decor, gx: -1.6 + (i % 4) * 0.9, gy: -1.6 + Math.floor(i / 4) * 0.9, seed: i + 1 })) }
      : {}),
    // `?shot=garden&stream` → a bigger plot + a pond-bulb + a channel running off the plot edge, to
    // verify the stream classification, the deep-water-to-edge bands, and the shimmer FX. (Dev only.)
    ...(p.has("stream")
      ? { gardenWater: streamShotCells(), plot: { cells: streamShotPlot() } }
      : {}),
    // `?shot=garden&shadowtest` → same modal problem as `terrain` below: the preset save's fishCaught pops
    // the "Angler" unlock, which dims + BLURS the whole scene, and the shadows ARE the subject here. Also
    // clear the decor/plants so nothing sits over the water.
    ...(p.has("shadowtest")
      ? { unlockedAchievements: ACHIEVEMENTS.map((a) => a.id), decor: [], garden: [] }
      : {}),
    // `?shot=garden&terrain` → a bare 5×5 plot with a SAND blob and a PEBBLE blob painted side by side on
    // the lawn (no decor, no water). Sand's whole job is a VALUE RELATIONSHIP with the grass it sits on, and
    // its one convergence risk is the pebble path, so neither can be judged from the sand alone — this is
    // the view that shows both at once. Dev shot only.
    ...(p.has("terrain")
      ? { gardenTerrain: terrainShotCells(), plot: { cells: streamShotPlot() }, gardenWater: [], decor: [], garden: [],
          // every achievement pre-unlocked: the preset save's fishCaught otherwise pops the "Angler" modal,
          // which dims + BLURS the scene — fatal for a shot whose whole subject is two adjacent colours.
          unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=garden&fullplot` → a garden owning EXACTLY `MAX_PLOT_CELLS` cells, so the expand tool's
    // AT-CAP behaviour can be driven headlessly. Reaching the cap by clicking would take 49 purchases and
    // several minutes of smoke-test wall clock; this puts the boundary one page-load away. The cell count
    // is read from the constant, never written out, so raising the cap re-points this preset for free.
    // Dev/shot only — `shotMode` is inert outside the Vite dev server.
    ...(p.has("fullplot") ? { plot: { cells: fullPlotCells() }, gardenWater: [], decor: [], garden: [] } : {}),
    // `?shot=garden&bigpond` → a MAX-SIZE plot painted nearly edge to edge with water. Not an art shot:
    // this is the SAVE PAYLOAD stress case. Water is one JSON key per sub-cell (sim/garden.ts), so this
    // preset produces the save that used to break cloud saving outright — over the 64 KiB `keepalive` cap
    // (every save failed, the store latched "offline") and over the server's old 256kb body limit (413).
    // The smoke test loads it and asserts the sync status does not go offline. Dev/shot only.
    ...(p.has("bigpond") ? { plot: { cells: fullPlotCells() }, gardenWater: bigPondCells(), decor: [], garden: [],
          // pre-unlock everything: this preset trips a pile of achievements at once and each queues a centre-screen
          // modal, which is noise for a step whose subject is a number in the save.
          unlockedAchievements: ACHIEVEMENTS.map((a) => a.id) }
      : {}),
    // `?shot=garden&waterextend` → reproduce the "buy a plot next to water" flow: a 2-cell plot with water
    // running off the shared/exposed edge, then the SAME extension the game runs on purchase carried into a
    // freshly-bought third cell. Used to eyeball the extended water + its sandy/gravel BANK for artifacts.
    ...(p.has("waterextend")
      ? (() => { const w = waterExtendShot(); return { gardenWater: [...w.water], plot: { cells: w.plot.cells } }; })()
      : {}),
    // `?shot=garden&butterflytest` → a PINE tree at origin + a daisy placed BEHIND-and-beside it, so a
    // seeded resting butterfly on the daisy can verify tree-occlusion (App seeds the butterfly to match).
    // `?shot=garden&treevar=<type>` → a row of the SAME tree type at different seeds, to eyeball the
    // per-seed variation (height/canopy/branch/shape). Dev-only.
    ...(p.has("treevar")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          decor: Array.from({ length: 6 }, (_, i) => {
            const t = (i - 2.5) * 1.5;   // spread across screen-x at a constant depth (gx+gy≈0)
            return { id: `tv-${i}`, decor: (p.get("treevar") || "pineTree"), gx: t, gy: -t, seed: i * 97 + 13 };
          }),
          garden: [],
        }
      : {}),
    // `?shot=garden&fencetest` → the fence art's own test case, because the three defects fences actually
    // have are all JOINS and REPEATS and none of them show on a single segment: a LONG RUN (does the
    // repeat moire?), a CORNER where two runs turn (does the shared post read as one post or as two
    // overlapping ones?), and a TEE (a third run arriving at an occupied post). A daisy stands beside it
    // as the roster's size reference. `fencetest=<style>` builds the same layout in any one fence style. Dev-only.
    ...(p.has("fencetest")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          decor: [],
          garden: [{ id: "ft-daisy", species: "daisy", gx: 0.4, gy: 0.4 }],
          fences: FENCETEST_KEYS,
          ...(p.get("fencetest") ? { fenceStyles: Object.fromEntries(FENCETEST_KEYS.map((k) => [k, p.get("fencetest")!])) } : {}),
        }
      : {}),
    // `?shot=garden&fencelayout=<edge>|<edge>…` → exactly these fences on an empty lawn, to rebuild a layout a player
    // reported (fence occlusion). An edge is `sx,sy,dir` with an optional `:style` (default rail). Dev-only.
    ...(p.get("fencelayout")
      ? (() => {
          const fences: string[] = [], fenceStyles: Record<string, string> = {};
          for (const item of p.get("fencelayout")!.split("|")) {
            const [k, st] = item.split(":");
            if (!k || !/^-?\d+,-?\d+,[hv]$/.test(k)) continue;
            fences.push(k);
            if (st && st !== "rail") fenceStyles[k] = st;
          }
          return { plot: { cells: streamShotPlot() }, gardenWater: [], decor: [], garden: [], fences, fenceStyles };
        })()
      : {}),
    // `?shot=garden&fencestyles` → every FENCE STYLE (sim/fenceStyles.ts) side by side, each as a run with a line
    // post, a corner and a terminal — the joins are where a style fails. A daisy for scale. Dev-only.
    ...(p.has("fencestyles")
      ? (() => {
          const styles = ["rail", "cottagePicket", "bamboo", "boxHedge", "drystone", "wroughtIron"];
          const fences: string[] = [];
          const fenceStyles: Record<string, string> = {};
          styles.forEach((st, i) => {
            const x0 = i % 2 === 0 ? -5 : 1, y = -4 + Math.floor(i / 2) * 3;
            const keys = [`${x0},${y},v`, `${x0 + 1},${y},v`, `${x0 + 2},${y},v`, `${x0 + 3},${y},h`];
            for (const k of keys) { fences.push(k); if (st !== "rail") fenceStyles[k] = st; }
          });
          return {
            plot: { cells: streamShotPlot() },
            gardenWater: [],
            decor: [],
            garden: [{ id: "fst-daisy", species: "daisy" as const, gx: 0.4, gy: 0.4 }],
            fences,
            fenceStyles,
          };
        })()
      : {}),
    // `?shot=garden&fencejoins=<style>` → that style MEETING each of the other five, one row per partner: a
    // STRAIGHT join (<style> then the partner, on one line), then the three L corners: W+S, E+S and W+N (the last
    // two are the depth ties — both arms on the same side of the shared post). <style> is always the W or E arm.
    // Six shots cover all 15 pairs in both orders. Dev-only.
    ...(p.has("fencejoins")
      ? (() => {
          const styles = ["rail", "cottagePicket", "bamboo", "boxHedge", "drystone", "wroughtIron"];
          const a = styles.includes(p.get("fencejoins")!) ? p.get("fencejoins")! : "rail";
          const fences: string[] = [];
          const fenceStyles: Record<string, string> = {};
          const put = (k: string, st: string) => { fences.push(k); if (st !== "rail") fenceStyles[k] = st; };
          // rows run down the screen (+1,+1 per lattice step), the four joins across it (+2,−1 each)
          styles.filter((s) => s !== a).forEach((b, r) => {
            const join = (f: number): [number, number] => [2 * (r - 2) + 2 * f - 3, 2 * (r - 2) - f + 1];
            let [x, y] = join(0); put(`${x - 1},${y},v`, a); put(`${x},${y},v`, b);    // straight
            [x, y] = join(1); put(`${x - 1},${y},v`, a); put(`${x},${y},h`, b);        // W+S
            [x, y] = join(2); put(`${x},${y},v`, a); put(`${x},${y},h`, b);            // E+S
            [x, y] = join(3); put(`${x - 1},${y},v`, a); put(`${x},${y - 1},h`, b);    // W+N
          });
          return { plot: { cells: streamShotPlot() }, gardenWater: [], decor: [], garden: [], fences, fenceStyles };
        })()
      : {}),
    // `?shot=garden&fencesort` → the fence DEPTH test: an L of fence with things standing CLOSE beside it on
    // both sides and OFFSET ALONG it — the case a single midpoint depth gets wrong (an item in front of the
    // rails, but beside the far end of a half, has a smaller gx+gy than the half's midpoint). Every item here
    // is legal under the fence rule (garden.ts decorPlaceBlock), so every one must sort by the side of the
    // line it stands on: front row in front of the rails, back row behind them. Dev-only.
    ...(p.has("fencesort")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          // the long run is "v" at gy = 0.5·SOIL_SLOT ≈ 1.167 from gx −3.5 to 5.83; the short run is "h" at
          // gx ≈ 5.83 from gy −3.5 up to the corner. FRONT = larger gy (or gx), BACK = smaller.
          fences: ["-2,0,v", "-1,0,v", "0,0,v", "1,0,v", "2,-2,h", "2,-1,h"],
          decor: [
            { id: "fs-pin",   decor: "pinwheel",  gx: -1.6, gy: 1.72, seed: 1 },   // front, 0.55 off the line
            { id: "fs-chime", decor: "windChime", gx: 1.7,  gy: 1.75, seed: 2 },   // front, 0.58 off the line
            { id: "fs-rock",  decor: "rock",      gx: -2.6, gy: 0.38, seed: 3 },   // back, 0.79 off the line
            { id: "fs-sign",  decor: "signpost",  gx: 3.6,  gy: 0.5,  seed: 4 },   // back, 0.67 off the line
            { id: "fs-pine",  decor: "pineTree",  gx: 4.7,  gy: -2.6, seed: 5 },   // behind the short run
            { id: "fs-bath",  decor: "birdBath",  gx: 6.95, gy: -1.2, seed: 6 },   // in front of the short run
          ],
          garden: [
            { id: "fs-d1", species: "daisy", gx: 0.2,  gy: 1.70 },   // front
            { id: "fs-d2", species: "daisy", gx: -0.6, gy: 0.62 },   // back
            { id: "fs-d3", species: "daisy", gx: 5.28, gy: -0.4 },   // back of the short run
          ],
        }
      : {}),
    ...(p.has("butterflytest")
      ? {
          plot: { cells: streamShotPlot() },   // a 5×5 plot so the daisy has ground to stand on
          gardenWater: [],                     // no pond — keep the tree/daisy area clear to read
          // `&notree` drops the pine so the seeded butterfly is visible on the daisy (proves it renders);
          // with the pine present the butterfly (behind the tree) must be OCCLUDED.
          // `&bath` swaps in a BIRD BATH at origin (a tall non-tree decor) to verify tall-decor occlusion.
          decor: p.has("notree") ? []
            : p.has("bath") ? [{ id: "bt-bath", decor: "birdBath", gx: 0, gy: 0, seed: 1 }]
            : [{ id: "bt-pine", decor: "pineTree", gx: 0, gy: 0, seed: 1 }],
          garden: [{ id: "bt-daisy", species: "daisy", gx: -1, gy: -1.6 }],
          // `&fence` → a fence segment IN FRONT of the daisy (nearer depth) to verify a butterfly resting on
          // that (behind) daisy is correctly OCCLUDED by the fence.
          ...(p.has("fence") ? { fences: ["-1,-1,h"] } : {}),
        }
      : {}),
    // `?shot=garden&sizetest` → daisies interleaved with the fountain + signpost + fence line so their
    // on-screen sizes can be compared against the DAISY (the size reference). `&trees` swaps in a tree row
    // (oak vs the rest) to compare tree sizes. Dev-only.
    ...(p.has("sizetest") && !p.has("trees")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          decor: [
            { id: "st-fountain", decor: "fountain", gx: -1.4, gy: -0.2, seed: 1 },
            { id: "st-sign", decor: "signpost", gx: 1.2, gy: -0.6, seed: 2 },
            { id: "st-birdbath", decor: "birdBath", gx: 1.4, gy: 1.0, seed: 3 },
          ],
          garden: [
            { id: "st-d1", species: "daisy", gx: -0.2, gy: -0.6 },
            { id: "st-d2", species: "daisy", gx: 0.2, gy: 0.2 },
            { id: "st-d3", species: "daisy", gx: -1.6, gy: 1.2 },
            { id: "st-d4", species: "daisy", gx: 2.0, gy: 0.2 },
          ],
          // a picket fence line along one tilled row (sx,sy,dir keys) to gauge fence height vs daisy.
          fences: ["0,-2,h", "1,-2,h", "-1,-2,h"],
        }
      : {}),
    // `?shot=garden&one=<decorId>` → a SINGLE decor item beside a pair of daisies on a clean plot, for the
    // decor SIZE AUDIT (compare each item's on-screen height against the daisy = 1.5 ft).
    // `&seeds` → a ROW of the SAME item at 5 different seeds (to check per-seed variation, e.g. tree
    // branch/canopy joins have no gaps). Dev-only.
    // `?shot=garden&specrow=goldStatue` (or botanicalMarker) → a ROW of that PER-SPECIES reward piece honouring
    // six very different silhouettes (a bush, a tall plant, a round fish, a long fish, a butterfly, a dragonfly)
    // on a clean plot, for judging the species picture, its fit and the statue's peg. (Dev only.)
    ...(p.get("specrow")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          garden: [],
          decor: ["plant:rose", "plant:sunflower", "fish:koi", "fish:pike", "insect:monarch", "insect:dragonfly"].map((species, i) => (
            { id: `spec-${i}`, decor: p.get("specrow")! as DecorId, gx: -2.1 + i * 0.85, gy: 2.1 - i * 0.85, seed: 1, species })),
        }
      : {}),
    ...(p.get("one")
      ? {
          plot: { cells: streamShotPlot() },
          gardenWater: [],
          decor: p.has("seeds")
            ? Array.from({ length: 5 }, (_, i) => ({ id: `audit-${i}`, decor: p.get("one")!, gx: (i - 2) * 1.4, gy: -(i - 2) * 1.4, seed: i * 41 + 3 }))
            : [{ id: "audit-one", decor: p.get("one")!, gx: 0.2, gy: 0.2, seed: 1 }],
          // `&planted` → instead of the two reference daisies, fill EVERY planting slot of the item with a
          // daisy (hostId + slot). A host's art has to hold up both empty and full, and the between-slot
          // filler on the long planter box in particular can only be judged against the plants it must
          // stay subordinate to. A non-host id just gets no plants.
          garden: p.has("seeds") ? []
            : p.has("planted")
              ? Array.from({ length: hostSlots(p.get("one")! as DecorId) }, (_, i) => (
                  { id: `audit-h${i}`, species: "daisy", gx: 0.2, gy: 0.2, hostId: "audit-one", slot: i }))
            : [
            { id: "audit-d1", species: "daisy", gx: -1.2, gy: 0.2 },
            { id: "audit-d2", species: "daisy", gx: -1.2, gy: 1.0 },
          ],
        }
      : {}),
    // `?shot=garden&showcase=<1..5>` → a fully dressed garden AND a fully stocked greenhouse for marketing
    // shots (sim/showcase.ts; captured by scripts/showcase.mjs). Last, so it overrides the preset above.
    ...(p.get("showcase") ? showcaseSave(Number(p.get("showcase"))) : {}),
  } as SaveData;
  return p.get("grow") ? { ...save, robot: false } : save;
}

/**
 * `&grow=<seconds>` — THE GROWING WAVE, for a video of the greenhouse coming to life (scripts/growvideo.mjs). The rack
 * DRAWS every pot from the seed to full bloom over a wave that sweeps the racks left to right in `seconds`, with a
 * little per-pot jitter so it reads as growth rather than a wipe. It schedules the PICTURE only (useGreenhouse
 * getPlantings), never the plants: real growth runs at each pot's own light/water/night speed, so a wave built from
 * banked grow-seconds drifted and every flower opened in one late burst. The pots hold at the seed until the capture
 * starts the wave (`window.__growWave.start()`), once the camera is framed. The robot is off: it would harvest the shot.
 */
let waveT0: number | null = null;
// read once: the scene loop asks every frame (usePixiScene's bake budget)
const GROW_WAVE = (() => { const n = Number(params()?.get("grow")); return Number.isFinite(n) && n > 2 ? n : null; })();
export function shotGrowWave(): number | null { return GROW_WAVE; }
if (shotGrowWave() != null && typeof window !== "undefined") {
  (window as unknown as { __growWave?: { start: () => void } }).__growWave = { start: () => { waveT0 = performance.now(); } };
}
/** The drawn progress (0..1) of pot `key` in the growing wave, or null when there is no wave. */
export function shotGrowProgress(key: string): number | null {
  const wave = shotGrowWave();
  if (wave == null) return null;
  if (waveT0 == null) return 0;
  const [r = 0, t = 0, c = 0] = key.split("-").map(Number);
  // only the racks IN FRAME grow (`&growRacks=1-3`, default all): the rest are drawn grown once, so they never compete
  // for the bake budget — each pot re-bakes through 24 growth steps, and an off-screen rack first in the slot order
  // starved the visible ones, which then jumped from bare soil to half-grown
  const [lo, hi] = (params()?.get("growRacks") ?? "0-99").split("-").map(Number) as [number, number];
  if (r < lo || r > hi) return 1;
  const last = Math.max(1, (Math.min(hi, 99) - lo + 1) * 4 - 1);
  const across = Math.min(1, ((r - lo) * 4 + c) / last);               // the racks in frame, left → right
  const jitter = (((r * 7 + t * 13 + c * 5) % 11) / 10 - 0.5) * 0.1;    // deterministic, ±5 % of the wave
  const begin = Math.max(0, 0.6 * across + jitter) * wave, span = 0.36 * wave;
  const x = Math.max(0, Math.min(1, ((performance.now() - waveT0) / 1000 - begin) / span));
  return x * x * (3 - 2 * x);                                           // eased: a slow sprout, a quick middle, a settle
}

/**
 * The preset's default decor, kept only where `canPlaceDecorAt` — the check the place flow runs — would
 * allow it, against the save's own plot, pond, plants and the items accepted before it. An illegal entry is
 * DROPPED with a warning rather than shown, so a preset can never again photograph a layout the game forbids.
 * Water clearance is the footprint fallback (sim can't reach the Pixi shadow measure), so leave a margin.
 */
function legalDecor(base: SaveData, want: DecorPlacement[]): DecorPlacement[] {
  const water = new Set(base.gardenWater ?? []);
  const out: DecorPlacement[] = [];
  for (const d of want) {
    if (canPlaceDecorAt(base.plot ?? startingPlot(), decorDef(d.decor).footprint, base.garden ?? [], out, d.gx, d.gy, water)) out.push(d);
    else console.warn(`[shot] preset decor "${d.id}" (${d.decor}) at ${d.gx},${d.gy} is not a legal placement — dropped`);
  }
  return out;
}

/**
 * EXACTLY `MAX_PLOT_CELLS` plot cells in a solid, 4-CONNECTED block centred on the origin — the at-cap
 * garden for the `&fullplot` shot.
 *
 * Laid out in a spiral out from (0,0) rather than as a rectangle, because the count is a constant that can
 * move and 50 does not factor into a pleasant rectangle; a spiral gives a connected blob for ANY count,
 * which is what `canSellCell`'s connectivity check and `edgeHandles` both assume. (0,0) is emitted first so
 * the free starter cell is always present, as every other code path expects.
 */
function fullPlotCells(): Array<{ cx: number; cy: number }> {
  const cells: Array<{ cx: number; cy: number }> = [];
  let cx = 0, cy = 0, dx = 1, dy = 0, run = 1;
  while (cells.length < MAX_PLOT_CELLS) {
    for (let leg = 0; leg < 2 && cells.length < MAX_PLOT_CELLS; leg++) {
      for (let i = 0; i < run && cells.length < MAX_PLOT_CELLS; i++) {
        cells.push({ cx, cy });
        cx += dx; cy += dy;
      }
      [dx, dy] = [-dy, dx];              // turn left
    }
    run++;
  }
  return cells;
}

/**
 * WATER PAINTED OVER EVERY CELL OF A MAX-SIZE PLOT — the save-payload worst case, generated from the same
 * two constants the real game uses so it tracks a cap raise or a WATER_DIV change for free.
 *
 * It fills each owned plot cell's own WATER_DIV × WATER_DIV block of sub-cells (cells are corner-indexed
 * off garden origin, so plot cell (cx,cy) owns sub-cells [cx·D − D/2, …) on each axis). That is the
 * arithmetic in the `sim/garden.ts` save-format note made concrete: 50 cells × 484 sub-cells ≈ 24k keys,
 * which was ~270 KB of per-key JSON — four times the 64 KiB `keepalive` cap — and packs to ~1.7 KB now.
 * Dev/shot only.
 */
function bigPondCells(): string[] {
  const H = WATER_DIV / 2;
  const out: string[] = [];
  for (const { cx, cy } of fullPlotCells()) {
    const x0 = cx * WATER_DIV - H, y0 = cy * WATER_DIV - H;
    for (let i = 0; i < WATER_DIV; i++) for (let j = 0; j < WATER_DIV; j++) out.push(`${x0 + i},${y0 + j}`);
  }
  return out;
}

/** A 5×5 block of plot cells centred on origin (for the stream shot). */
function streamShotPlot(): Array<{ cx: number; cy: number }> {
  const cells: Array<{ cx: number; cy: number }> = [];
  for (let cx = -2; cx <= 2; cx++) for (let cy = -2; cy <= 2; cy++) cells.push({ cx, cy });
  return cells;
}

/** Painted terrain blobs on the lawn — SAND on the left, PEBBLE on the right, close enough to read in
 *  one frame against the grass and against each other, plus DIRT below them — dirt's job is to read as the
 *  same turned earth as the plot's own soil skirt, so it must be judged against the grass AND against the
 *  earth wall in the same frame. A STONE patch is laid RIGHT UP AGAINST the pebble blob: those two are the
 *  pair most at risk of converging (both are grey paths), and the only view that settles it is the one
 *  where the loose aggregate and the laid pavers share an edge. The natural blobs get a lumpy (not
 *  circular) radius so the Chaikin-smoothed contour and its transition stipple are actually exercised.
 *  Dev shot only. */
function terrainShotCells(): Record<string, TerrainType> {
  const out: Record<string, TerrainType> = {};
  const blob = (cx: number, cy: number, r: number, type: TerrainType, phase: number) => {
    for (let x = cx - r - 2; x <= cx + r + 2; x++) for (let y = cy - r - 2; y <= cy + r + 2; y++) {
      const dx = x - cx, dy = y - cy;
      const ang = Math.atan2(dy, dx);
      const rr = r * (1 + 0.18 * Math.sin(ang * 3 + phase) + 0.10 * Math.sin(ang * 5 - phase));
      if (dx * dx + dy * dy <= rr * rr) out[`${x},${y}`] = type;
    }
  };
  blob(-16, 3, 10, "sand", 0.7);
  blob(11, -3, 9, "pebble", 2.1);
  blob(-2, 14, 10, "dirt", 1.4);
  // STONE is laid as a square block (it is a paver path, not a blob) butted against the pebble patch.
  for (let x = 14; x <= 22; x++) for (let y = 7; y <= 15; y++) out[`${x},${y}`] = "stone";
  return out;
}

/** A pond bulb + a thin channel running out to a plot edge (water-cell keys). Dev shot only. */
function streamShotCells(): string[] {
  const out = new Set<string>();
  const add = (wx: number, wy: number) => out.add(`${wx},${wy}`);
  // pond bulb (a filled disc) centred near the plot origin.
  for (let x = -12; x <= 12; x++) for (let y = -12; y <= 12; y++) if (x * x + y * y <= 130) add(x, y);
  // channel: ~5-wide, from the bulb out toward the SE edge (well beyond ±55 on the 5×5 plot).
  for (let t = 0; t <= 80; t++) { const x = 6 + t, y = 6 + Math.round(t * 0.4); for (let w = -2; w <= 2; w++) add(x, y + w); }
  return [...out];
}

/**
 * Reproduce the "buy a plot next to water" scenario reported to produce glitchy gravel/bank shapes. Start from
 * a small plot with water flowing off an exposed edge, then run the REAL `extendStreamsAcrossExpansion` as the
 * new cell is bought — so the shot shows exactly the extended water + bank the game generates. Dev-only.
 */
function waterExtendShot(): { water: Set<string>; plot: Plot } {
  const H = WATER_DIV / 2;               // half a plot-cell, in water sub-cells (= 11)
  // start FROM SCRATCH: a single cell at origin (cx=0,cy=0), water painted against its exposed SOUTH edge.
  const oldPlot: Plot = { cells: [{ cx: 0, cy: 0 }] };
  const water = new Set<string>();
  // a NARROW, BENT channel (~7 sub-cells wide → a stream, so it extends) meandering down to (and off) the
  // south edge — the bend makes the edge-touching band a strongly concave C-loop, which is exactly where the
  // old whole-loop smoothing warped the water-side edge into glitchy gravel shapes.
  const southEdge = 2 * H;               // south boundary of cell cy=0 is at wy = 2·H (=22)
  for (let wy = 3; wy <= southEdge - 1; wy++) {
    const bend = Math.round(Math.sin((wy - 3) / 5) * 4);   // lateral meander
    for (let wx = -3; wx <= 3; wx++) water.add(`${wx + bend},${wy}`);
  }
  // then buy the cell to the SOUTH (cx=0,cy=1) — its purchase swallows the water's exposed south outlet, so
  // the game extends the water across the new cell (this is where the reported bank/gravel artifacts appear).
  const newPlot: Plot = { cells: [...oldPlot.cells, { cx: 0, cy: 1 }] };
  const grown = extendStreamsAcrossExpansion(water, oldPlot, newPlot);
  return { water: grown, plot: newPlot };
}
